import { lazy, Suspense, useLayoutEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Brain,
  Building2,
  Clock3,
  CreditCard,
  Database,
  DollarSign,
  Globe,
  HardDrive,
  LifeBuoy,
  Loader2,
  Mail,
  Plus,
  Server,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  XCircle,
  Zap,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import RefreshFailedNotice from '../../components/RefreshFailedNotice'
import { platformOverviewResponse } from '../../lib/types'
import Flags from 'react-phone-number-input/flags'
import countryLabels from 'react-phone-number-input/locale/en.json'
import { Pie3D, TrendChart } from '../../components/charts/Charts'
import { DONUT_PALETTE } from '../../components/charts/palette'
import { useChartHover } from '../../components/charts/use-chart-hover'
import { ChartTooltip } from '../../components/charts/ChartTooltip'
import { useAuthStore } from '../../stores/authStore'
import { formatBytes, formatDayMonth, greetingName, timeOfDay } from '../../lib/dashboard-helpers'
import { datePattern, formatDateTime, formatNumber, formatPattern } from '../../lib/format'

// The world map (50m TopoJSON + react-simple-maps/d3) is loaded on demand so it
// stays out of the entry chunk every user downloads.
const CompaniesWorldMap = lazy(() => import('./CompaniesWorldMap'))

export function SuperAdminDashboard() {
  const user = useAuthStore((s) => s.user)
  const overviewQuery = useQuery({
    queryKey: ['platform-overview'],
    queryFn: async () => {
      const res = await api.get('/super-admin/platform/overview')
      return platformOverviewResponse.parse(res.data)
    },
    refetchInterval: 60_000,
  })

  return (
    <div>
      {/* Hero greeting — heading + role chip + subtitle. The live
       *  date/time stamp that used to sit on the right was removed
       *  per design request — the global header already carries a
       *  live clock pill, so repeating a static timestamp inside
       *  the page body read as duplicate noise. Matches the
       *  Company Admin / Sub Admin / Employee Home dashboards
       *  which also dropped their hero date stamps. */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-3xl font-medium text-[color:var(--color-ink-strong)]">
              Good {timeOfDay()}, {user?.fullName ? greetingName(user.fullName) : 'Master Admin'}
            </h1>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-normal border border-[color:var(--color-accent-border)] bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent)] shrink-0">
              Master Admin
            </span>
          </div>
          <p className="mt-1 text-[color:var(--color-ink-muted)]">
            How TaskMagix is doing today.
          </p>
        </div>
      </div>

      {/* [QA-14] Cached data first: a failed background refetch keeps the
       *  last good numbers on screen with a non-blocking notice; the
       *  blocking error is only for a load that never produced data. */}
      {overviewQuery.data ? (
        <>
          {overviewQuery.isError ? (
            <RefreshFailedNotice
              error={overviewQuery.error}
              onRetry={() => overviewQuery.refetch()}
            />
          ) : null}
          <DashboardBody data={overviewQuery.data} />
        </>
      ) : overviewQuery.isLoading ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] inline-flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading platform data…
        </p>
      ) : overviewQuery.isError ? (
        <p className="text-sm text-[color:var(--color-priority-urgent)]">
          {extractApiError(overviewQuery.error).message}
        </p>
      ) : null}
    </div>
  )
}

// ─── Tone palette ───────────────────────────────────────────────────
// Per-tone tile + halo + sparkline-stroke triplet. Cloned from the
// Company Admin Dashboard so the two panels share the exact same
// visual rhythm — vivid mid-tone glyph on a light tinted chip, a
// soft blurred halo of the same family bleeding out behind it, and a
// sparkline stroke that picks up the glyph hue.
// In dark mode each tone uses a SOFT per-tone wash (15% tint + tinted glyph +
// faint tone border) and the coloured halo glow is switched off — matching the
// Company Admin and User dashboards. (The earlier uniform white/6% neutral tile
// left linear glyphs nearly invisible; QA-reported and fixed there first.)
// Light mode keeps the vivid tinted chips.
const TONE_CLASSES = {
  gold: {
    tile: 'bg-[#FEF3C7] text-[#D97706] border border-[#FDE68A] dark:bg-amber-500/15 dark:text-amber-300 dark:border-amber-400/20',
    halo: 'from-[#FEF3C7] to-transparent dark:from-transparent',
    stroke: '#D97706',
  },
  sky: {
    tile: 'bg-[#DBEAFE] text-[#2563EB] border border-[#BFDBFE] dark:bg-sky-500/15 dark:text-sky-300 dark:border-sky-400/20',
    halo: 'from-[#DBEAFE] to-transparent dark:from-transparent',
    stroke: '#2563EB',
  },
  emerald: {
    tile: 'bg-[#D1FAE5] text-[#059669] border border-[#A7F3D0] dark:bg-emerald-500/15 dark:text-emerald-300 dark:border-emerald-400/20',
    halo: 'from-[#D1FAE5] to-transparent dark:from-transparent',
    stroke: '#059669',
  },
  amber: {
    tile: 'bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] dark:bg-orange-500/15 dark:text-orange-300 dark:border-orange-400/20',
    halo: 'from-[#FFEDD5] to-transparent dark:from-transparent',
    stroke: '#EA580C',
  },
  rose: {
    tile: 'bg-[#FFE4E6] text-[#E11D48] border border-[#FECDD3] dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-400/20',
    halo: 'from-[#FFE4E6] to-transparent dark:from-transparent',
    stroke: '#E11D48',
  },
  violet: {
    tile: 'bg-[#EDE9FE] text-[#7C3AED] border border-[#DDD6FE] dark:bg-violet-500/15 dark:text-violet-300 dark:border-violet-400/20',
    halo: 'from-[#EDE9FE] to-transparent dark:from-transparent',
    stroke: '#7C3AED',
  },
  pink: {
    tile: 'bg-[#FCE7F3] text-[#DB2777] border border-[#FBCFE8] dark:bg-pink-500/15 dark:text-pink-300 dark:border-pink-400/20',
    halo: 'from-[#FCE7F3] to-transparent dark:from-transparent',
    stroke: '#DB2777',
  },
  slate: {
    tile:
      'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]',
    halo: 'from-[color:var(--color-canvas)] to-transparent',
    stroke: '#64748B',
  },
}

// ─── Delta primitive ────────────────────────────────────────────────
// The Sparkline + smoothPath helpers that lived here previously have
// been removed along with the in-card 14-day trend curve — testers
// reported the soft wave under each KPI tile competed visually with
// the headline number. Trend context is now carried purely by the
// `DeltaChip` below ("▲ +N this week" / "▼ -N this week"). To bring
// the sparkline back, restore the `smoothPath` + `Sparkline`
// components from git history (commit before the SuperAdmin sparkline
// removal) and wire them inside `KpiHeroTile`.

// `DeltaChip` removed — the "+N this week" percentage delta read as
// misleading noise on fresh-tenant baselines. KPI tiles now show
// the headline number alone.

/** Hero KPI tile — premium variant with big value and an optional
 *  "View →" link. Modelled on the Company Admin Dashboard's
 *  `KpiHeroTile` so the platform panel reads as one family
 *  with the tenant-admin one.
 *
 *  The earlier `sparkline` prop (14-day trend curve painted behind
 *  the value) was removed — the soft wave competed with the headline
 *  number. Trend context now flows through `delta` only. Callers may
 *  keep passing sparkline data on the wire; this tile ignores it.
 *
 *  No `min-h` — the parent grid auto-stretches all siblings to the
 *  tallest natural height, which is the correct behaviour. */
function KpiHeroTile({
  label,
  value,
  icon: Icon,
  tone,
  to,
  hint,
}) {
  const tones = TONE_CLASSES[tone]
  const body = (
    <>
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-70 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 min-h-[2.5rem] leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-strong)] tabular-nums leading-none">
            {typeof value === 'number' ? formatNumber(value) : value}
          </p>
          {hint ? (
            <p className="mt-2 text-[13px] text-[color:var(--color-ink-muted)]">
              {hint}
            </p>
          ) : null}
          {/* Delta chip removed — the "+N this week" percentage read
           *  as misleading noise against a near-zero baseline on
           *  fresh tenants. Headline number stands alone. */}
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
      {/* mt-auto pins the "View" row to the bottom across all four
       *  hero tiles even when sibling tiles have variable
       *  hint/delta content above. The earlier `mb-2` was for the
       *  sparkline curve below — no longer needed. */}
      {to ? (
        <div className="relative mt-auto pt-3 inline-flex items-center gap-1 text-[13px] font-normal text-[color:var(--color-accent-strong)] opacity-80 group-hover:opacity-100">
          View
          <ArrowRight
            size={12}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </div>
      ) : null}
    </>
  )

  // `p-5` only — the previous `pb-14` reserved vertical room for the
  // absolutely-positioned Sparkline at the bottom of the tile. With
  // the sparkline removed there's no reason to leave that bottom
  // buffer; tiles now hug their content for a tighter, denser feel.
  return to ? (
    <Link
      to={to}
      className="group flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 hover:border-[color:var(--color-accent-border)] hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
    >
      {body}
    </Link>
  ) : (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
      {body}
    </div>
  )
}

/** Billing placeholder tile — visual real-estate reserved for the
 *  upcoming MRR / Paid Tenants / Trial Tenants / Failed Payments
 *  metrics. Renders an em-dash where the value goes plus a small
 *  "coming soon" badge so the empty slot doesn't read as a data bug.
 *  Style mirrors `KpiHeroTile` (same tile/halo chrome) but with a
 *  muted inner state — no link, no delta chip, no sparkline. Swap
 *  for a real `KpiHeroTile` call when billing ingestion ships. */
// ─── Growth chart palette ──────────────────────────────────────────
//
// The Signups Area Chart that previously rendered here has been
// removed per design request — the platform admin didn't find the
// 14-day trend useful and asked for the dashboard to land on the
// Plan Distribution donut alone. The chart constants
// (`SIGNUPS_CHART_GRADIENT_TOP`, `SIGNUPS_CHART_COLOR`, the
// `SignupsAreaChartCard` component, and the `smoothPathPoints` /
// `fmtDayShort` helpers it depended on) were dropped along with it.
// Restore from git history if a signups chart is ever brought back.


/** Navy gradient stops — match the Company Admin's
 *  `prodTasksGradient` (`DashboardPage.jsx > prodTasksGradient`).
 *  Used by the Signups column chart so the two panels share the
 *  same chart vocabulary. */
const SIGNUPS_CHART_GRADIENT_TOP = '#1E3A8A'
const SIGNUPS_CHART_GRADIENT_BOTTOM = '#0B1E63'

/** Format a YYYY-MM-DD bucket day as "6 Jun" ("Jun 6" for a person who
 *  reads month-first dates) — used for the column chart's X-axis labels.
 *  The bucket is a calendar day, so it is never zone-shifted. Falls back
 *  to the raw string if the input can't be parsed so the label slot never
 *  goes blank. */
function fmtDayShort(day) {
  if (!day) return ''
  return formatPattern(day, datePattern().startsWith('MM') ? 'MMM d' : 'd MMM') || String(day)
}

/** Tab toggle options for the Company Signups chart window. The
 *  backend ships 45 days of signup buckets (see
 *  `SPARKLINE_DAYS` in `platform.service.js`); each tab slices the
 *  tail of that series so the operator can zoom in (15 days) or out
 *  (30 / 45 days) without a refetch. */
const SIGNUPS_WINDOW_OPTIONS = [
  { days: 15, label: '15 days' },
  { days: 30, label: '30 days' },
  { days: 45, label: '45 days' },
]

/** Column (vertical bar) chart for daily company signups. Tab toggle
 *  in the header picks the window size (15 / 30 / 45 days); the
 *  card slices the tail of the backend's 45-day series accordingly.
 *  One column per day, height proportional to that day's signup
 *  count. Y-axis carries integer ticks (0 / mid / max rounded to a
 *  "nice" number); X-axis carries day labels at the start / middle
 *  / end of the window. Empty / zero-day rows render as a faint
 *  baseline strip so the time axis stays uninterrupted. */
function SignupsColumnChartCard({ sparkline }) {
  // Selected window in days. Default to 15 — the "zoomed-in" view
  // — because most operators care most about the very recent
  // signups; the 30/45 day windows are for trend-spotting.
  const [windowDays, setWindowDays] = useState(15)
  // Shared chart-hover infra — sibling-dim + cursor-tracked tooltip.
  const { ref, hover, track, clear } = useChartHover()
  // Measure the wrapper so the column chart FILLS the card width at a FIXED
  // height. Previously the viewBox was a static 640×220 with `h-auto`, so on a
  // wide monitor the SVG scaled its height up with the card width (~480px tall,
  // dominating the page). Driving the viewBox width from the measured px width
  // keeps 1 unit = 1px — the bars spread across the real width while the height
  // stays pinned at H. `ref` is the `relative` wrapper useChartHover already
  // attaches; its clientWidth is exactly the SVG's available width.
  const [chartW, setChartW] = useState(640)
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const measure = () => {
      const w = Math.round(el.clientWidth)
      if (w > 0) setChartW(w)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  // Slice the tail of the 45-day series to the selected window. If
  // the backend ships fewer days than requested (older deploys, or
  // a fresh install with no history), `slice(-N)` just returns the
  // whole array, so the chart never errors on short inputs.
  const fullSeries = sparkline ?? []
  const data = fullSeries.slice(-windowDays)
  const total = data.reduce((sum, d) => sum + (d.count ?? 0), 0)

  // Height is fixed (shares the Company Admin bar chart's cadence); the width
  // tracks the card (measured above) so the columns fill it instead of the
  // chart growing taller with the card. Floor the width so a very narrow card
  // never yields a cramped / negative plot area.
  const W = Math.max(chartW, 360)
  const H = 220
  const padL = 36
  const padR = 12
  const padT = 16
  const padB = 28
  const plotW = W - padL - padR
  const plotH = H - padT - padB

  // "Nice number" Y-axis maximum so a peak of 3 reads as 5 / 10 /
  // 25 instead of an awkward 7.
  const rawMax = Math.max(0, ...data.map((d) => d.count ?? 0))
  const niceMax = (() => {
    if (rawMax <= 1) return 1
    if (rawMax <= 5) return 5
    if (rawMax <= 10) return 10
    const exp = Math.pow(10, Math.floor(Math.log10(rawMax)))
    const mult = Math.ceil(rawMax / exp)
    return mult * exp
  })()

  const yFor = (n) => padT + plotH - (n / niceMax) * plotH

  // Bar widths. With 14 bars and ~580px plot width that's ~32px per
  // bar with a 1/3 gap — a clean "Power BI-style" rhythm.
  const slotW = data.length > 0 ? plotW / data.length : 0
  const barGapRatio = 0.3
  // Cap the bar width so columns keep a tidy rhythm on a wide card rather than
  // ballooning to ~70px; narrower cards keep the gap ratio. `barLeft` centres
  // the bar in its slot, so a capped bar just gains symmetric side gap.
  const barW = Math.min(slotW * (1 - barGapRatio), 56)
  const barLeft = (i) => padL + i * slotW + (slotW - barW) / 2

  // Y-axis tick labels — always three (0 / mid / max). niceMax/2
  // is rounded to keep the mid label integer-clean for small ranges.
  const yMid = Math.round(niceMax / 2)
  const yTicks = niceMax >= 2 ? [0, yMid, niceMax] : [0, niceMax]

  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 h-full flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
            <TrendingUp
              size={18}
              className="text-[color:var(--color-primary)]"
              aria-hidden="true"
            />
            Company Signups
          </h3>
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)]">
            Last {windowDays} days
          </p>
        </div>
        {/* `shrink-0` here was the bug QA hit: the segmented control (~200px)
         *  plus the total block (~110px) plus the gap is ~330px, and with
         *  `shrink-0` the group refuses to shrink OR wrap, so on a narrow card
         *  it overflows the right edge and clips "total in last 15 days" to
         *  "total in last 1". Allowed to wrap below `sm` — the tabs take one
         *  line and the total sits under them, both fully readable. Desktop
         *  keeps the original single-line, never-shrink behaviour. */}
        <div className="flex flex-wrap items-start gap-3 sm:flex-nowrap sm:gap-4 sm:shrink-0">
          {/* Window tabs — segmented control. Each tab picks how many
           *  tail days of the backend's 45-day series the chart
           *  renders. The selected tab is painted with the primary
           *  brand fill; idle tabs are muted so the strip reads as
           *  a single grouped control rather than three free buttons. */}
          <div
            role="tablist"
            aria-label="Signups window"
            className="inline-flex items-center rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] p-0.5"
          >
            {SIGNUPS_WINDOW_OPTIONS.map((opt) => {
              const selected = opt.days === windowDays
              return (
                <button
                  key={opt.days}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => setWindowDays(opt.days)}
                  className={
                    'px-2.5 py-1 rounded-md text-[12px] font-medium tabular-nums transition-colors cursor-pointer ' +
                    (selected
                      ? 'bg-[color:var(--color-primary)] text-white shadow-sm'
                      : 'text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)]')
                  }
                >
                  {opt.label}
                </button>
              )
            })}
          </div>
          <div className="text-right">
            <p className="font-display text-3xl text-[color:var(--color-ink-strong)] tabular-nums leading-none m-0">
              {formatNumber(total)}
            </p>
            {/* Caption binds to `windowDays` so the operator can see at
             *  a glance that the headline number above is the sum for
             *  THIS window — not the all-time total. Important when
             *  the same number renders across two adjacent tabs (e.g.
             *  the last signup was 16 days ago, so "15 days" reads 0
             *  and "30 days" reads 1) — without the window in the
             *  caption, the operator might think the chart broke. */}
            {/* `whitespace-nowrap` so the phrase never breaks mid-sentence
             *  once it has room — it either fits on one line or the group
             *  above wraps it onto its own. */}
            <p className="mt-1 text-[12px] text-[color:var(--color-ink-muted)] m-0 whitespace-nowrap">
              total in last {windowDays} days
            </p>
          </div>
        </div>
      </div>

      {data.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-12 text-center italic">
          No signup data yet for this window.
        </p>
      ) : (
        <>
          <div ref={ref} className="relative">
            <svg
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="xMidYMid meet"
              className="w-full"
              style={{ height: H }}
              aria-label="Daily new company signups, column chart"
              role="img"
            >
              <defs>
                <linearGradient
                  id="signupsBarGradient"
                  x1="0%"
                  y1="0%"
                  x2="0%"
                  y2="100%"
                >
                  <stop offset="0%" stopColor={SIGNUPS_CHART_GRADIENT_TOP} />
                  <stop offset="100%" stopColor={SIGNUPS_CHART_GRADIENT_BOTTOM} />
                </linearGradient>
              </defs>

              {/* Y-axis gridlines + tick labels */}
              {yTicks.map((tick) => {
                const y = yFor(tick)
                return (
                  <g key={tick}>
                    <line
                      x1={padL}
                      y1={y}
                      x2={padL + plotW}
                      y2={y}
                      stroke="var(--color-rule)"
                      strokeWidth="1"
                      strokeDasharray={tick === 0 ? '0' : '2 3'}
                    />
                    <text
                      x={padL - 8}
                      y={y}
                      textAnchor="end"
                      dominantBaseline="middle"
                      fontSize="11"
                      fill="var(--color-ink-muted)"
                      className="tabular-nums"
                    >
                      {tick}
                    </text>
                  </g>
                )
              })}

              {/* Plot area frame — left axis + baseline */}
              <line
                x1={padL}
                y1={padT}
                x2={padL}
                y2={padT + plotH}
                stroke="var(--color-rule)"
                strokeWidth="1"
              />
              <line
                x1={padL}
                y1={padT + plotH}
                x2={padL + plotW}
                y2={padT + plotH}
                stroke="var(--color-rule)"
                strokeWidth="1"
              />

              {/* One column per day. Zero-count days render as a
               *  flat 2px baseline strip so the time axis stays
               *  continuous (an absent bar would read as "missing
               *  data" rather than "zero signups"). Hovering a column
               *  dims its siblings and surfaces the shared tooltip. */}
              <g className="group">
                {data.map((d, i) => {
                  const count = d.count ?? 0
                  const x = barLeft(i)
                  const barH = (count / niceMax) * plotH
                  const y = count === 0 ? padT + plotH - 2 : padT + plotH - barH
                  const renderH = count === 0 ? 2 : barH
                  const payload = {
                    color: count === 0 ? '#94A3B8' : SIGNUPS_CHART_GRADIENT_TOP,
                    label: fmtDayShort(d.day),
                    value: `${formatNumber(count)} signup${
                      count === 1 ? '' : 's'
                    }`,
                  }
                  return (
                    <rect
                      key={i}
                      x={x}
                      y={y}
                      width={barW}
                      height={renderH}
                      fill={
                        count === 0
                          ? 'var(--color-rule)'
                          : 'url(#signupsBarGradient)'
                      }
                      stroke={count === 0 ? 'none' : 'var(--chart-outline)'}
                      strokeWidth={1}
                      rx={count === 0 ? '0' : '2'}
                      className="transition-opacity duration-150 group-hover:opacity-40 hover:!opacity-100"
                      onMouseEnter={track(payload)}
                      onMouseMove={track(payload)}
                      onMouseLeave={clear}
                    />
                  )
                })}
              </g>

              {/* X-axis labels — first / middle / last day */}
              {data.length >= 2 ? (
                <>
                  <text
                    x={barLeft(0) + barW / 2}
                    y={padT + plotH + 18}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--color-ink-muted)"
                    className="tabular-nums"
                  >
                    {fmtDayShort(data[0].day)}
                  </text>
                  {data.length >= 5 ? (
                    <text
                      x={
                        barLeft(Math.floor((data.length - 1) / 2)) + barW / 2
                      }
                      y={padT + plotH + 18}
                      textAnchor="middle"
                      fontSize="11"
                      fill="var(--color-ink-muted)"
                      className="tabular-nums"
                    >
                      {fmtDayShort(
                        data[Math.floor((data.length - 1) / 2)].day,
                      )}
                    </text>
                  ) : null}
                  <text
                    x={barLeft(data.length - 1) + barW / 2}
                    y={padT + plotH + 18}
                    textAnchor="middle"
                    fontSize="11"
                    fill="var(--color-ink-muted)"
                    className="tabular-nums"
                  >
                    {fmtDayShort(data[data.length - 1].day)}
                  </text>
                </>
              ) : null}
            </svg>
            {hover ? <ChartTooltip {...hover} /> : null}
          </div>

          {/* Footer stats removed — the headline "total signups"
           *  number in the card header already carries the
           *  at-a-glance signal; per-day averages and peaks read as
           *  noise on a low-volume window. */}
        </>
      )}
    </div>
  )
}


/** Count line under each slice's % in the Master Admin 3D pies. */
const companiesLabel = (v) => `${formatNumber(v)} ${v === 1 ? 'company' : 'companies'}`

/** Plan Distribution — the tenant split across plan tiers (Free / Trial /
 *  Gold / Enterprise / …) as a flat 3D pie (shared `Pie3D`, `uniform` +
 *  text callouts): every slice the same thick extrusion in the brand blue
 *  palette, with each plan's % + name + company count printed beside it,
 *  so no separate legend is needed. Header carries the title on the left and
 *  "N companies" on the right — matches Company Admin's `AnalyticsCard`
 *  shape. The card uses `h-full flex flex-col` so it stretches to the same
 *  height as the card beside it; the pie's canvas grows taller by itself
 *  when one side carries many plan labels. */
function PlanDistributionDonutCard({ byPlan }) {
  // Filter to plans with at least one tenant + sort by backend
  // sortOrder so the legend reads in tier order (Free / Trial /
  // Gold / Enterprise). Zero-count plans drop off — the catalogue
  // inventory belongs on the Plans admin page, not the dashboard.
  const segments = (byPlan ?? [])
    .filter((p) => (p.count ?? 0) > 0)
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((p, i) => ({
      // Defensive fallback — the backend already labels no-plan tenants
      // "No plan", but never let an empty name render a blank legend row.
      label: p.name || 'No plan',
      value: p.count,
      color: DONUT_PALETTE[i % DONUT_PALETTE.length],
    }))
  const total = segments.reduce((sum, s) => sum + (s.value ?? 0), 0)

  return (
    <div className="h-full flex flex-col rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      {/* Header — title left, "N tenants" right. Mirrors the
       *  Company Admin's AnalyticsCard header shape. */}
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          Plan Distribution
        </h3>
        <p className="text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
          {formatNumber(total)} {total === 1 ? 'company' : 'companies'}
        </p>
      </div>

      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center py-6">
          <p className="text-sm text-[color:var(--color-ink-muted)] text-center italic">
            No companies on any plan yet.
          </p>
        </div>
      ) : (
        // flex-1 fills the remaining card height so the pie centres in
        // whatever room the grid cell has.
        <div className="flex-1 flex items-center justify-center">
          <Pie3D
            segments={segments}
            uniform
            callout="text"
            width={560}
            formatValue={companiesLabel}
          />
        </div>
      )}
    </div>
  )
}

/** Company Status — Active / Invited / Suspended / Offboarded as a flat 3D
 *  pie (same `Pie3D` treatment as Plan Distribution), from
 *  `data.tenants.byDisplayStatus`: the Companies page's DISPLAY status, so an
 *  active company whose owner hasn't accepted the setup invite counts as
 *  Invited. Every status is covered — those with no companies can't be a
 *  slice, so they're listed with 0 under the pie. Colours follow the Company
 *  Admin status badges (Members grid chip) — the same ones the Companies page
 *  status column + the shared StatusBadge already use: Active green, Invited
 *  amber, Suspended red, Offboarded grey (a finished state, not an alarm). */
const COMPANY_STATUS_META = [
  { key: 'active', label: 'Active', color: 'var(--color-success)' },
  { key: 'invited', label: 'Invited', color: 'var(--color-warning)' },
  { key: 'suspended', label: 'Suspended', color: 'var(--color-priority-urgent)' },
  { key: 'offboarded', label: 'Offboarded', color: 'var(--color-ink-muted)' },
]
function CompanyStatusDonutCard({ byStatus }) {
  const all = COMPANY_STATUS_META.map((m) => ({
    label: m.label,
    value: byStatus?.[m.key] ?? 0,
    color: m.color,
  }))
  const segments = all.filter((s) => s.value > 0)
  const empty = all.filter((s) => s.value === 0)
  const total = segments.reduce((sum, s) => sum + (s.value ?? 0), 0)

  return (
    <div className="h-full flex flex-col rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          Company Status
        </h3>
        <p className="text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
          {formatNumber(total)} {total === 1 ? 'company' : 'companies'}
        </p>
      </div>

      {total === 0 ? (
        <div className="flex-1 flex items-center justify-center py-6">
          <p className="text-sm text-[color:var(--color-ink-muted)] text-center italic">
            No companies yet.
          </p>
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <Pie3D
            segments={segments}
            uniform
            callout="text"
            width={560}
            formatValue={companiesLabel}
          />
          {/* Statuses with no companies — still covered, with their 0. */}
          {empty.length > 0 ? (
            <ul className="m-0 flex list-none flex-wrap justify-center gap-x-5 gap-y-1 p-0 text-[13px] text-[color:var(--color-ink-muted)]">
              {empty.map((s) => (
                <li key={s.label} className="inline-flex items-center gap-1.5">
                  <span
                    aria-hidden="true"
                    className="h-2.5 w-2.5 rounded-full"
                    style={{ backgroundColor: s.color, border: '1px solid var(--chart-outline)' }}
                  />
                  {s.label}
                  <span className="font-medium text-[color:var(--color-ink-strong)] tabular-nums">0</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
    </div>
  )
}

/** Platform growth/activity trend card — a single-series line from one of
 *  the 45-day sparkline arrays already on the overview payload. These are
 *  signup / event VOLUME over time (not a live DAU — the platform feed does
 *  not carry daily-active users). */
function PlatformTrendCard({ title, subtitle, sparkline, color, emptyMessage }) {
  const points = sparkline ?? []
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
      <div className="flex items-baseline justify-between mb-4 gap-3">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h3>
        <p className="text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
          {subtitle}
        </p>
      </div>
      <TrendChart
        labels={points.map((p) => p.day)}
        series={[{ name: title, color, data: points.map((p) => p.count) }]}
        labelFormat={formatDayMonth}
        emptyMessage={emptyMessage}
      />
    </div>
  )
}

function BillingPlaceholderTile({ label, icon: Icon, tone }) {
  const tones = TONE_CLASSES[tone] ?? TONE_CLASSES.slate
  return (
    <div className="flex flex-col relative overflow-hidden rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 min-h-[140px]">
      <div
        aria-hidden="true"
        className={
          'pointer-events-none absolute -top-12 -right-10 h-36 w-36 rounded-full blur-2xl opacity-40 bg-gradient-to-br ' +
          tones.halo
        }
      />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display text-sm uppercase tracking-wider text-[color:var(--color-ink-muted)] font-medium m-0 min-h-[2.5rem] leading-tight">
            {label}
          </h3>
          <p className="mt-2 font-display text-4xl text-[color:var(--color-ink-muted)] tabular-nums leading-none">
            —
          </p>
          <span className="mt-3 inline-flex items-center gap-1 rounded-full bg-[color:var(--color-canvas)] border border-[color:var(--color-rule)] px-2 py-0.5 text-[11px] font-medium text-[color:var(--color-ink-muted)]">
            Coming soon
          </span>
        </div>
        <div
          className={
            'h-12 w-12 rounded-xl flex items-center justify-center shrink-0 opacity-60 ' +
            tones.tile
          }
        >
          <Icon size={24} />
        </div>
      </div>
    </div>
  )
}

/** Flag + country name for the Top Countries list. The name TRUNCATES with an
 *  ellipsis (and a title tooltip) so a long name like "United Arab Emirates"
 *  degrades gracefully in the fixed-width column instead of being hard-clipped
 *  mid-word. Uses the same flag set + English names as the shared CountryCell. */
function CountryLabel({ code }) {
  const iso = String(code).toUpperCase()
  const label = countryLabels[iso] ?? iso
  const Flag = Flags[iso]
  return (
    <span className="flex min-w-0 items-center gap-2" title={label}>
      {Flag ? (
        <Flag title={label} style={{ width: 18, height: 12, flexShrink: 0 }} />
      ) : (
        <span aria-hidden="true" className="shrink-0 text-[10px]">
          🌐
        </span>
      )}
      <span className="truncate text-sm text-[color:var(--color-ink)]">{label}</span>
    </span>
  )
}

/** Tenants grouped by detected country — a ranked horizontal-bar list.
 *  Renders flags + names via CountryLabel so they match the Companies
 *  table; `unknown` rows (no detected country) render as "Not set". The
 *  bar is scaled to the busiest country; the number shows the raw count
 *  plus its share of all companies. Caps at the top 15 so a long tail
 *  can't push the card off-screen — the remainder is summarised below. */
function CountriesCard({ byCountry, total }) {
  const rows = byCountry ?? []
  // The 'unknown' bucket ("Not set") is a row but not a country — count it out
  // so this header agrees with the world map's country count below.
  const countryCount = rows.filter((r) => r?.code && r.code !== 'unknown').length
  const MAX_ROWS = 15
  const shown = rows.slice(0, MAX_ROWS)
  const hidden = rows.length - shown.length
  // Percentages read against the real company total so they sum to ~100%
  // even when the long tail is truncated; the bar scales to the busiest
  // SHOWN country so the leader always fills the track.
  const grand = total || rows.reduce((sum, r) => sum + r.count, 0)
  const max = Math.max(1, ...shown.map((r) => r.count))
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5 h-full">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
          <Globe
            size={18}
            className="text-[color:var(--color-primary)]"
            aria-hidden="true"
          />
          Top Countries
        </h3>
        <span className="shrink-0 text-sm text-[color:var(--color-ink-muted)] tabular-nums">
          {countryCount} {countryCount === 1 ? 'country' : 'countries'}
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm italic text-[color:var(--color-ink-muted)]">
          No country data yet.
        </p>
      ) : (
        <ul className="m-0 list-none space-y-2.5 p-0">
          {shown.map((r) => {
            const pct = grand > 0 ? Math.round((r.count / grand) * 100) : 0
            const barPct = Math.round((r.count / max) * 100)
            return (
              <li key={r.code} className="flex items-center gap-3">
                <div className="w-44 shrink-0 min-w-0 sm:w-52">
                  {r.code === 'unknown' ? (
                    <span className="text-sm text-[color:var(--color-ink-muted)]">
                      Not set
                    </span>
                  ) : (
                    <CountryLabel code={r.code} />
                  )}
                </div>
                <div className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[color:var(--color-surface-2)]">
                  <div
                    className="h-full rounded-full bg-[color:var(--color-primary)]"
                    style={{ width: `${barPct}%` }}
                  />
                </div>
                <span className="w-24 shrink-0 text-right text-sm tabular-nums text-[color:var(--color-ink-strong)]">
                  {formatNumber(r.count)}
                  <span className="ml-1 text-xs text-[color:var(--color-ink-muted)]">
                    {pct}%
                  </span>
                </span>
              </li>
            )
          })}
          {hidden > 0 ? (
            <li className="pt-1 text-xs text-[color:var(--color-ink-muted)]">
              +{hidden} more {hidden === 1 ? 'country' : 'countries'}
            </li>
          ) : null}
        </ul>
      )}
    </div>
  )
}

/** Section heading — mirrors the Company Admin Dashboard's
 *  `SectionHeader` exactly so every chapter divider on the SuperAdmin
 *  dashboard matches the tenant-admin one (font-display title + muted
 *  hint, no uppercase eyebrow). */
function SectionHeader({ title, hint, action }) {
  return (
    <div className="flex items-baseline justify-between gap-3 mt-10 mb-4">
      <div>
        <h2 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0">
          {title}
        </h2>
        {hint ? (
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            {hint}
          </p>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  )
}

// ─── Helpers for sparkline-driven KPI summaries ─────────────────────

// Note: `sparklineDelta` used to compute a chip-ready first-vs-last
// percent change for the Active Companies / Active Users tiles. It
// was removed alongside the `delta` props on those tiles per the
// "remove captions from KPI cards" cleanup — the sparkline shape
// itself communicates the trend. Re-introduce here if a future tile
// needs the chip back.

/** "5m / 2h / 3d ago" relative timestamp used by the System Health
 *  strip footer line. Returns an em-dash for null / invalid input so
 *  callers don't have to guard. */
function fmtAgo(input) {
  if (!input) return '—'
  const d = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(d.getTime())) return '—'
  const diff = Math.max(0, Date.now() - d.getTime())
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

// ─── Dashboard body ─────────────────────────────────────────────────

function DashboardBody({ data }) {
  // ─── KPI headline numbers ───────────────────────────────────────
  // The Platform Events · 14 days tile was hidden, so the
  // `eventsSparkline` derivation + `sumSeries` helper that backed
  // its headline are gone. Restore both if the tile is brought back.
  //
  // The old `tenantsHint` / `tenantsDelta` / `usersDelta` captions
  // were also dropped from the surviving KPI tiles by request.
  // Suspended / offboarded counts are still available on the
  // Companies page; the dashboard intentionally stays sparse here.

  return (
    <>
      {/* ─── Section 1: Hero KPI strip ────────────────────────────
       *  Two premium tiles: Active Companies · Active Users.
       *  The Master Admin asked for the "Platform
       *  Events · 14 days" tile to be hidden — the 14-day event
       *  total read as a low-signal aggregate next to the live
       *  Active Companies / Active Users counts, and the
       *  cross-company audit feed is still one click away via the
       *  "Recent Platform Events" section + Audit Logs sidebar
       *  entry. Restore by re-adding a fourth `<KpiHeroTile>` here
       *  and bumping the grid back to `lg:grid-cols-4`.
       *  ──────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* KPI tiles — captions trimmed.
         *  The tiles previously surfaced a `hint` line ("All
         *  companies healthy", "18 total accounts", etc.) and a
         *  `delta` chip ("— No change · signups · 14 days") under
         *  the headline number. The Master Admin asked for a
         *  cleaner card: label + number only. The underlying
         *  KpiHeroTile still supports both props for reuse on
         *  other dashboards; we just don't pass them here. */}
        <KpiHeroTile
          label="Active Companies"
          value={data.tenants.byStatus.active}
          icon={Building2}
          tone="violet"
          to="/super-admin/companies"
        />
        <KpiHeroTile
          label="Total Companies"
          value={data.tenants.total}
          icon={Building2}
          tone="emerald"
          to="/super-admin/companies"
        />
        <KpiHeroTile
          label="Active Users"
          value={data.users.active}
          icon={Users}
          tone="sky"
          to="/super-admin/all-users"
        />
        <KpiHeroTile
          label="Total Users"
          value={data.users.total}
          icon={Users}
          tone="amber"
          to="/super-admin/all-users"
        />
      </div>

      {/* ─── Growth row — company signups + geographic spread, side by
       *  side on lg+ (stacks below lg).
       *    Left  — daily company signups column chart ("how fast?")
       *    Right — Companies by Country ranked list ("where?")
       *  Both read the overview payload (`tenants.signupsSparkline` +
       *  `tenants.byCountry`). The chart sizes its SVG to its own card
       *  width, so it reflows cleanly into the narrower half-width
       *  column. `items-stretch` (grid default) + `h-full` on both cards
       *  keeps the pair the same height. */}
      <SectionHeader
        title="Growth"
        hint="New company signups and where customers are based"
      />
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SignupsColumnChartCard
          sparkline={data.tenants.signupsSparkline ?? []}
        />
        <CountriesCard
          byCountry={data.tenants.byCountry ?? []}
          total={data.tenants.total}
        />
      </div>

      {/* ─── Geographic distribution — Power BI-style world choropleth ─
       *  of companies by country. Full-width so the map stays legible.
       *  Reuses the SAME canonical tenants.byCountry aggregation as the
       *  Top Countries list above (no second count); the map total
       *  reconciles with the Total Companies KPI. */}
      <SectionHeader
        title="Geographic Distribution"
        hint="Where companies using the platform are based, worldwide"
      />
      <div className="mb-6">
        <Suspense
          fallback={
            <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-5">
              <p className="py-16 text-center text-sm text-[color:var(--color-ink-muted)]">
                Loading map…
              </p>
            </div>
          }
        >
          <CompaniesWorldMap
            byCountry={data.tenants.byCountry ?? []}
            total={data.tenants.total}
          />
        </Suspense>
      </div>

      {/* Distribution — plan mix + account-status health, side by side.
       *  Both read numbers already on the overview payload
       *  (`tenants.byPlan` / `tenants.byStatus`); no extra request. */}
      <SectionHeader
        title="Distribution"
        hint="How companies are split across plan tiers and account status"
      />
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlanDistributionDonutCard byPlan={data.tenants.byPlan ?? []} />
        <CompanyStatusDonutCard
          byStatus={data.tenants.byDisplayStatus ?? { ...(data.tenants.byStatus ?? {}), invited: 0 }}
        />
      </div>

      {/* ─── Platform Activity ──────────────────────────────────────
       *  45-day new-user + activity-volume trends from the sparkline
       *  arrays already on the payload (users.signupsSparkline /
       *  events.eventsSparkline) — no extra request. Signup / event
       *  volume over time, not a live DAU (the feed has no daily-active). */}
      <SectionHeader
        title="Platform Activity"
        hint="New users and platform-wide activity over the last 45 days"
      />
      <div className="mb-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PlatformTrendCard
          title="New Users"
          subtitle="Last 45 days"
          sparkline={data.users.signupsSparkline ?? []}
          color="var(--color-accent-strong)"
          emptyMessage="No new users in the last 45 days"
        />
        <PlatformTrendCard
          title="Platform Activity"
          subtitle="Audit events · last 45 days"
          sparkline={data.events?.eventsSparkline ?? []}
          color="var(--color-info)"
          emptyMessage="No activity in the last 45 days"
        />
      </div>

      {/* ─── Billing (placeholder) ────────────────────────────────
       *  Five tiles reserved for the upcoming billing integration —
       *  MRR, Total Revenue (lifetime), paid tenants, trial tenants,
       *  failed payments. Backend has no Subscription / Invoice /
       *  PaymentAttempt models yet, so values render as em-dashes
       *  with a "Billing integration coming soon" hint. When
       *  Razorpay/Stripe ingestion ships, the placeholders swap in
       *  for real numbers without further layout churn — keeps the
       *  visual real estate reserved so the dashboard doesn't
       *  reflow when billing lands.
       *
       *  Order: MRR sits first (the headline SaaS health metric);
       *  Total Revenue (cumulative lifetime) sits next so the two
       *  revenue numbers read together; then the supporting cohort
       *  counts (Paid / Trial) and finally the risk signal
       *  (Failed Payments).
       *
       *  Layout: 3 cards on row 1 (MRR / Total Revenue / Paid) and
       *  2 cards LEFT-ALIGNED on row 2 (Trial / Failed Payments).
       *  Built on a 6-column grid where each card spans 2 columns —
       *  so row 1 fills exactly (3 × 2 = 6) and row 2 flows naturally
       *  from column 1, leaving columns 5-6 empty on the right.
       *  Falls back to a 2-column grid on medium screens and a single
       *  column on mobile. Card width is intentionally similar to the
       *  previous 5-wide layout so the visual rhythm of the
       *  dashboard's other rows is preserved. */}
      <SectionHeader
        title="Billing"
        hint="Revenue and payment-health metrics — billing integration coming soon"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
        <div className="lg:col-span-2">
          <BillingPlaceholderTile
            label="Monthly Recurring Revenue"
            icon={DollarSign}
            tone="emerald"
          />
        </div>
        <div className="lg:col-span-2">
          <BillingPlaceholderTile
            label="Total Revenue"
            icon={TrendingUp}
            tone="violet"
          />
        </div>
        <div className="lg:col-span-2">
          <BillingPlaceholderTile
            label="Paid Companies"
            icon={CreditCard}
            tone="sky"
          />
        </div>
        {/* Row 2 — left-aligned. First card flows into columns 1-2,
         *  next into 3-4; columns 5-6 stay empty on the right. */}
        <div className="lg:col-span-2">
          <BillingPlaceholderTile
            label="Trial Companies"
            icon={Clock3}
            tone="amber"
          />
        </div>
        <div className="lg:col-span-2">
          <BillingPlaceholderTile
            label="Failed Payments"
            icon={XCircle}
            tone="rose"
          />
        </div>
      </div>

      {/* ─── Section 2: Today / Action Needed ─────────────────────
       *  Two-up — Support Tickets inbox on the left, Platform Risks
       *  on the right. This is the "what needs my attention right
       *  now" row, so it sits immediately under the headline KPIs.
       *  ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Action Needed"
        hint="Live support load and platform-wide risk signals"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SupportTicketsCard tickets={data.supportTickets} />
        <RisksCard risks={data.risks ?? []} />
      </div>

      {/* ─── Section 3: Workload Health ───────────────────────────
       *  Two-up — Storage Usage by Tenant on the left, AI Usage by
       *  Tenant on the right. Both lists are capped at the top 8
       *  server-side.
       *  ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Workload Health"
        hint="Top companies by storage and AI consumption"
      />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StorageByTenantCard rows={data.storageUsageByTenant ?? []} />
        <AiUsageByTenantCard rows={data.aiUsageByTenant ?? []} />
      </div>

      {/* ─── Section 4: Recent Activity ───────────────────────────
       *  Cross-tenant audit feed. Empty state is friendly because
       *  new platforms legitimately won't have events on day one.
       *  ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Recent Platform Events"
        hint="Cross-company audit trail · last 10 events"
        action={
          <Link
            to="/super-admin/audit-logs"
            className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
          >
            Full log
            <ArrowRight size={12} />
          </Link>
        }
      />
      <RecentEventsCard events={data.events?.recent ?? []} />

      {/* ─── Section 5: Quick Actions ─────────────────────────────
       *  2-col grid of the five most-used platform-admin actions.
       *  Replaces the old Tenant Status tile row — those counts
       *  now live in the Active Tenants KPI tile's hint.
       *  ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="Quick Actions"
        hint="Jump straight to the most common platform tasks"
      />
      <QuickActionsGrid />

      {/* ─── System Health (supplementary footer strip) ───────────
       *  Compact 4-up tile row showing the latest liveness probes
       *  for the API, database, Redis and email-queue subsystems.
       *  Sits below the five spec'd sections because it's
       *  supplementary — but is mentioned in the user spec so we
       *  include it here as a footer.
       *  ──────────────────────────────────────────────────────── */}
      <SectionHeader
        title="System Health"
        hint="Live readiness of the platform's core subsystems"
      />
      <SystemHealthStrip health={data.systemHealth} />
    </>
  )
}

// ─── Section 2: Support Tickets card ────────────────────────────────

/** Left-side card of the "Action Needed" row. Headline open-count,
 *  two sub-tiles for urgent / overdue, and a top-5 list of recent
 *  tickets. Empty state when there's nothing to triage. */
function SupportTicketsCard({ tickets }) {
  const open = tickets?.openCount ?? 0
  const urgent = tickets?.urgentCount ?? 0
  const overdue = tickets?.overdueCount ?? 0
  const recent = (tickets?.recent ?? []).slice(0, 5)

  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
            <LifeBuoy
              size={18}
              className="text-[color:var(--color-accent)]"
              aria-hidden="true"
            />
            Support Tickets
          </h3>
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)]">
            Inbox health · top 5 most recent
          </p>
        </div>
        <Link
          to="/super-admin/support-tickets"
          className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
        >
          View inbox
          <ArrowRight size={12} />
        </Link>
      </div>

      {/* Headline + sub-tiles row */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        {/* Open = info (blue), Urgent = danger (red), Overdue = warning
         *  (amber). All three use the semantic *-bg / *-border / text
         *  tokens so they read consistently AND adapt to dark mode. */}
        <div className="rounded-lg border border-[color:var(--color-info-border)] bg-[color:var(--color-info-bg)] p-3">
          <p className="text-[11px] uppercase tracking-wider font-medium text-[color:var(--color-info)]">
            Open
          </p>
          <p className="mt-1 font-display text-3xl tabular-nums leading-none text-[color:var(--color-info)]">
            {formatNumber(open)}
          </p>
        </div>
        <div className="rounded-lg border border-[color:var(--color-danger-border)] bg-[color:var(--color-danger-bg)] p-3">
          <p className="text-[11px] uppercase tracking-wider font-medium text-[color:var(--color-danger)]">
            Urgent
          </p>
          <p className="mt-1 font-display text-3xl tabular-nums leading-none text-[color:var(--color-danger)]">
            {formatNumber(urgent)}
          </p>
        </div>
        {/* Deep-links to the Support Tickets page's matching "Overdue" filter
         *  (open tickets older than 7 days) so the card is actionable and the
         *  Dashboard and module stay consistent. */}
        <Link
          to="/super-admin/support-tickets?status=overdue"
          className="rounded-lg border border-[color:var(--color-warning-border)] bg-[color:var(--color-warning-bg)] p-3 transition-colors hover:border-[color:var(--color-warning)]"
        >
          <p className="text-[11px] uppercase tracking-wider font-medium text-[color:var(--color-warning)]">
            Overdue
          </p>
          <p className="mt-1 font-display text-3xl tabular-nums leading-none text-[color:var(--color-warning)]">
            {formatNumber(overdue)}
          </p>
        </Link>
      </div>

      {/* Recent ticket list — or friendly empty state when inbox clear */}
      {recent.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-4 text-sm text-[color:var(--color-ink-muted)] italic gap-2">
          <Sparkles size={14} aria-hidden="true" />
          Inbox clear
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--color-rule)] mt-1">
          {recent.map((t) => (
            <li
              key={t.ticketId}
              className="flex items-center justify-between gap-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                  {t.subject}
                </p>
                <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
                  {t.tenantName ?? 'Platform'}
                </p>
              </div>
              <div className="inline-flex items-center gap-2 shrink-0">
                <span
                  className={
                    'text-[11px] uppercase tracking-wider font-medium px-2 py-0.5 rounded border ' +
                    priorityChipClass(t.priority)
                  }
                >
                  {t.priority}
                </span>
                <Link
                  to={`/super-admin/support-tickets/${t.ticketId}`}
                  className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1"
                >
                  Open
                  <ArrowRight size={12} />
                </Link>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Priority chip palette — mirrors the SupportTicketsPage PRIORITY_TONE
 *  but as a full chip (bg + border + text) so the dashboard list reads
 *  at a glance without needing extra context. */
function priorityChipClass(priority) {
  switch (priority) {
    case 'urgent':
      return 'bg-[#FFE4E6] text-[#E11D48] border-[#FECDD3] dark:bg-white/[0.06] dark:text-rose-300 dark:border-white/10'
    case 'high':
      return 'bg-[#FFEDD5] text-[#EA580C] border-[#FED7AA] dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10'
    case 'normal':
      return 'bg-[#DBEAFE] text-[#2563EB] border-[#BFDBFE] dark:bg-white/[0.06] dark:text-sky-300 dark:border-white/10'
    case 'low':
    default:
      return 'bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border-[color:var(--color-rule)]'
  }
}

// ─── Section 2: Platform Risks card ─────────────────────────────────

const RISK_KIND_META = {
  'storage-over-cap': { label: 'Storage', tone: 'amber' },
  'ai-hot': { label: 'AI Usage', tone: 'violet' },
  'payment-failed': { label: 'Payment', tone: 'rose' },
  'trial-ending': { label: 'Trial', tone: 'sky' },
}

/** Right-side card of the "Action Needed" row. Top-5 risks ordered
 *  danger → warning by the server. Each row shows a severity dot, a
 *  kind chip, the tenant and a one-line detail. */
function RisksCard({ risks }) {
  const top = (risks ?? []).slice(0, 5)

  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 flex flex-col">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
            <AlertTriangle
              size={18}
              className="text-[#EA580C]"
              aria-hidden="true"
            />
            Platform Risks
          </h3>
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)]">
            Over-limits, payment failures, trials ending
          </p>
        </div>
        <Link
          to="/super-admin/companies"
          className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
        >
          All companies
          <ArrowRight size={12} />
        </Link>
      </div>

      {top.length === 0 ? (
        <div className="flex-1 flex items-center justify-center py-12 text-sm text-[color:var(--color-ink-muted)] italic gap-2">
          <Sparkles size={14} aria-hidden="true" />
          No risks detected
        </div>
      ) : (
        <ul className="divide-y divide-[color:var(--color-rule)]">
          {top.map((r, i) => {
            const meta = RISK_KIND_META[r.kind] ?? { label: r.kind, tone: 'slate' }
            const dotColor =
              r.severity === 'danger' ? '#E11D48' : '#EA580C'
            const chipTone = TONE_CLASSES[meta.tone]
            return (
              <li
                key={`${r.tenantId}-${r.kind}-${i}`}
                className="flex items-center justify-between gap-3 py-3"
              >
                <div className="min-w-0 flex items-center gap-3">
                  <span
                    aria-hidden="true"
                    className="h-2 w-2 rounded-full shrink-0"
                    style={{ backgroundColor: dotColor }}
                  />
                  <span
                    className={
                      'text-[11px] uppercase tracking-wider font-medium px-2 py-0.5 rounded shrink-0 ' +
                      chipTone.tile
                    }
                  >
                    {meta.label}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                      {r.tenantName}
                    </p>
                    <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
                      {r.detail}
                    </p>
                  </div>
                </div>
                <Link
                  to={`/super-admin/companies?focus=${r.tenantId}`}
                  className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
                >
                  Investigate
                  <ArrowRight size={12} />
                </Link>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ─── Section 3: Storage Usage by Tenant ─────────────────────────────

/** Left card of the Workload Health row. Renders the top 8 tenants
 *  by storage consumption as a horizontal-bar list. When the tenant's
 *  plan has no storage cap (`percentOfCap` null) we render a muted
 *  tick bar at 25% so the row still has visual weight. */
function StorageByTenantCard({ rows }) {
  const top = (rows ?? []).slice(0, 8)
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 min-h-[220px]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
            <HardDrive
              size={18}
              className="text-[color:var(--color-accent)]"
              aria-hidden="true"
            />
            Storage Usage by Company
          </h3>
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)]">
            Top 8 consumers · % of plan cap
          </p>
        </div>
        <Link
          to="/super-admin/storage"
          className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
        >
          All companies
          <ArrowRight size={12} />
        </Link>
      </div>

      {top.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-8 text-center italic">
          No storage usage recorded yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {top.map((r) => {
            const hasCap = r.percentOfCap != null
            const pct = hasCap ? Math.min(100, Math.max(0, r.percentOfCap)) : 25
            const overCap = hasCap && r.percentOfCap >= 90
            const barColor = !hasCap
              ? '#94A3B8'
              : overCap
              ? '#E11D48'
              : r.percentOfCap >= 70
              ? '#EA580C'
              : '#2563EB'
            return (
              <li key={r.tenantId}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <div className="min-w-0 flex items-center gap-2">
                    <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                      {r.tenantName}
                    </p>
                    {r.planTier ? (
                      <span className="text-[11px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)] shrink-0">
                        {r.planTier}
                      </span>
                    ) : (
                      // No-plan company → the canonical amber "Awaiting plan"
                      // badge (matching the Companies page), not a blank space.
                      <span
                        className="text-[11px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded bg-[color:var(--color-warning-bg)] text-[color:var(--color-warning)] border border-[color:var(--color-warning-border)] shrink-0"
                        title="Awaiting plan selection — this self-signup company hasn't chosen a plan yet."
                      >
                        Awaiting plan
                      </span>
                    )}
                  </div>
                  <p className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
                    {formatBytes(r.usedBytes)}
                    {hasCap ? (
                      <span className="ml-1">· {Math.round(r.percentOfCap)}%</span>
                    ) : (
                      <span className="ml-1 italic">· unlimited</span>
                    )}
                  </p>
                </div>
                <div className="h-2 rounded-full bg-[color:var(--color-rule)] overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: barColor,
                      opacity: hasCap ? 1 : 0.4,
                    }}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}

// ─── Section 3: AI Usage by Tenant ──────────────────────────────────

/** Right card of the Workload Health row. Renders the top 8 tenants
 *  by AI credits this calendar month — one solid bar per company,
 *  coloured by company identity so the card reads as a per-tenant
 *  ranking rather than a per-provider categorisation.
 *
 *  Previously the bar was stacked by AI provider (Anthropic / Google
 *  / Heuristic). Testers read that as "this card is categorising my
 *  company under Google or Heuristic" — even when the bar was 100%
 *  Anthropic — and asked for the provider lens to be dropped. The
 *  per-provider breakdown is still available on the dedicated AI
 *  Usage page (linked via "All tenants →" in the header). */
const COMPANY_BAR_PALETTE = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#059669', // emerald
  '#D97706', // amber
  '#E11D48', // rose
  '#0EA5E9', // sky
  '#DB2777', // pink
  '#4F46E5', // indigo
]

function AiUsageByTenantCard({ rows }) {
  const top = (rows ?? []).slice(0, 8)
  return (
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6 min-h-[220px]">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-lg text-[color:var(--color-ink-strong)] font-medium tracking-tight m-0 inline-flex items-center gap-2">
            <Brain
              size={18}
              className="text-[#7C3AED]"
              aria-hidden="true"
            />
            AI Usage by Company
          </h3>
          <p className="mt-1 text-[13px] text-[color:var(--color-ink-muted)]">
            Top 8 · credits this month
          </p>
        </div>
        <Link
          to="/super-admin/ai-usage"
          className="text-[13px] font-normal text-[color:var(--color-accent-strong)] hover:underline inline-flex items-center gap-1 shrink-0"
        >
          All companies
          <ArrowRight size={12} />
        </Link>
      </div>

      {top.length === 0 ? (
        <p className="text-sm text-[color:var(--color-ink-muted)] py-8 text-center italic">
          No AI usage recorded yet this month.
        </p>
      ) : (
        <>
          {/* No legend — every row carries its own colour swatch
           *  inline with the company name, which scales better than
           *  an 8-chip header strip would. Bar width represents each
           *  company's share of the heaviest user's credits, so the
           *  top bar always reads as full while smaller customers
           *  scale proportionally below. */}
          <ul className="space-y-3">
            {(() => {
              const maxCredits = Math.max(
                1,
                ...top.map((r) => r.creditsThisMonth ?? 0),
              )
              return top.map((r, idx) => {
                const color = COMPANY_BAR_PALETTE[idx % COMPANY_BAR_PALETTE.length]
                const credits = r.creditsThisMonth ?? 0
                const pct = (credits / maxCredits) * 100
                return (
                  <li key={r.tenantId}>
                    <div className="flex items-baseline justify-between gap-2 mb-1">
                      <p className="text-sm text-[color:var(--color-ink-strong)] truncate min-w-0 inline-flex items-center gap-2">
                        <span
                          aria-hidden="true"
                          className="h-2 w-2 rounded-sm shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        {r.tenantName}
                      </p>
                      <p className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0">
                        {formatNumber(credits)} credits
                      </p>
                    </div>
                    {credits === 0 ? (
                      <div className="h-2 rounded-full bg-[color:var(--color-rule)]" />
                    ) : (
                      <div className="h-2 rounded-full overflow-hidden bg-[color:var(--color-rule)]">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: color,
                          }}
                        />
                      </div>
                    )}
                  </li>
                )
              })
            })()}
          </ul>
        </>
      )}
    </div>
  )
}

// ─── Section 4: Recent Platform Events feed ─────────────────────────

/** Full-width Recent Platform Events feed. Each row: actor + tenant
 *  · action chip · timestamp. Empty state is friendly because new
 *  platforms legitimately won't have events on day one. */
function RecentEventsCard({ events }) {
  if (events.length === 0) {
    return (
      <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-6">
        <div className="flex items-center justify-center gap-2 text-sm text-[color:var(--color-ink-muted)] italic py-4">
          <Sparkles size={14} aria-hidden="true" />
          No platform events recorded yet.
        </div>
      </div>
    )
  }
  return (
    // Cap the card at ~24rem and let the inner list scroll. Without
    // the cap, 10 audit-event rows pushed the page past the fold and
    // the Quick Actions grid below ended up below the viewport even
    // on standard laptop displays. The `max-h-96` keeps the section
    // compact while still showing 4-5 events at rest — the rest are
    // reached via the inner scrollbar OR the "Full log" link in the
    // section header. The outer container holds the border + padding;
    // the inner `<div>` does the actual scrolling so the divide-y
    // lines never leak past the rounded corners.
    <div className="rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-2">
      <div className="max-h-96 overflow-y-auto pr-1">
        <ul className="divide-y divide-[color:var(--color-rule)]">
        {events.map((e) => {
          const actor = e.actorName ?? 'System'
          const tenant = e.tenantName ?? 'Platform'
          return (
            <li
              key={e.id}
              // The fixed-width timestamp on the right squeezes the
              // actor/tenant/action text down to one or two characters when the
              // row is horizontal but narrow, so the timestamp stacks BELOW the
              // text and the text gets the full row width.
              //
              // Goes horizontal at `sm` (640px). The sidebar is off-canvas
              // below `lg`, so the row gets the full viewport there — ~560px
              // of usable width after padding, of which the timestamp takes
              // ~150px and the icon ~48px, leaving ~360px for the text. That
              // is comfortable, so there is no reason to hold the stacked
              // layout any longer.
              className="flex flex-col sm:flex-row sm:items-center sm:justify-between px-3 py-3 rounded-md hover:bg-[color:var(--color-canvas)] transition-colors gap-1 sm:gap-3"
            >
              {/* `flex-1` so the text claims the space the timestamp doesn't,
               *  rather than sizing to content and then being crushed. */}
              <div className="min-w-0 flex-1 flex items-center gap-3">
                <span className="h-9 w-9 rounded-lg bg-[#FFEDD5] text-[#EA580C] border border-[#FED7AA] flex items-center justify-center shrink-0 dark:bg-white/[0.06] dark:text-amber-300 dark:border-white/10">
                  <Activity size={18} />
                </span>
                <div className="min-w-0">
                  <p className="text-sm text-[color:var(--color-ink-strong)] truncate">
                    <span className="font-medium">{actor}</span>
                    <span className="text-[color:var(--color-ink-muted)]"> · {tenant}</span>
                  </p>
                  <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
                    {/* Action chip rendered inline so the visual
                     *  hierarchy reads "who" first, "what" second.
                     *  Audit actions are dotted.snake names — humanise
                     *  them lightly without trying to be exhaustive. */}
                    <span className="inline-flex items-center text-[12px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded mr-1 bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
                      {e.action.replace(/[._]/g, ' ')}
                    </span>
                    {e.resourceType ? (
                      <span className="ml-1">on {e.resourceType}</span>
                    ) : null}
                  </p>
                </div>
              </div>
              <span className="text-[13px] text-[color:var(--color-ink-muted)] tabular-nums shrink-0 pl-12 sm:pl-0">
                {formatDateTime(e.createdAt)}
              </span>
            </li>
          )
        })}
      </ul>
      </div>
    </div>
  )
}

// ─── Section 5: Quick Actions grid ──────────────────────────────────

const QUICK_ACTIONS = [
  {
    label: 'Create Company',
    hint: 'Onboard a new company',
    to: '/super-admin/companies?action=create',
    icon: Plus,
    tone: 'emerald',
  },
  {
    label: 'View Companies',
    hint: 'Browse every company',
    to: '/super-admin/companies',
    icon: Building2,
    tone: 'violet',
  },
  {
    label: 'Support Tickets',
    hint: 'Triage the inbox',
    to: '/super-admin/support-tickets',
    icon: LifeBuoy,
    tone: 'sky',
  },
  {
    label: 'Audit Logs',
    hint: 'Full cross-company trail',
    to: '/super-admin/audit-logs',
    icon: ShieldCheck,
    tone: 'amber',
  },
  {
    label: 'View Plans',
    hint: 'Manage the catalogue',
    to: '/super-admin/plans',
    icon: Zap,
    tone: 'pink',
  },
]

/** Five most-used platform-admin actions in a responsive 2-col grid.
 *  Replaces the old Tenant Status three-tile row — the active /
 *  suspended counts now live in the Active Tenants KPI tile's hint. */
function QuickActionsGrid() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {QUICK_ACTIONS.map((a) => {
        const tones = TONE_CLASSES[a.tone]
        const Icon = a.icon
        return (
          <Link
            key={a.to}
            to={a.to}
            className="group flex items-center gap-3 rounded-xl border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4 hover:border-[color:var(--color-accent-border)] hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          >
            <div
              className={
                'h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ' +
                tones.tile
              }
            >
              <Icon size={20} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)] truncate">
                {a.label}
              </p>
              <p className="text-[13px] text-[color:var(--color-ink-muted)] truncate">
                {a.hint}
              </p>
            </div>
            <ArrowRight
              size={14}
              className="ml-auto text-[color:var(--color-ink-muted)] transition-transform group-hover:translate-x-0.5 shrink-0"
            />
          </Link>
        )
      })}
    </div>
  )
}

// ─── System Health footer strip ─────────────────────────────────────

/** Compact 4-up tile row showing the latest liveness status for the
 *  API, database, Redis and email-queue subsystems. Each subsystem
 *  reports one of three states: green (ok=true), red (ok=false), or
 *  muted grey (ok=null, "Not monitored" — e.g. Redis isn't configured
 *  in this deployment). When the probe carries a metric — uptime,
 *  latency, queue depth — it's shown inline so the operator sees the
 *  signal alongside the dot, not just a green check. */
function SystemHealthStrip({ health }) {
  const tiles = [
    { key: 'api', label: 'API', icon: Server, value: health?.api ?? null },
    { key: 'database', label: 'Database', icon: Database, value: health?.database ?? null },
    { key: 'redis', label: 'Redis', icon: Zap, value: health?.redis ?? null },
    {
      key: 'emailQueue',
      label: 'Email Queue',
      icon: Mail,
      value: health?.emailQueue ?? null,
    },
    { key: 'storage', label: 'Storage', icon: HardDrive, value: health?.storage ?? null },
    { key: 'stripe', label: 'Payments', icon: CreditCard, value: health?.stripe ?? null },
    { key: 'ai', label: 'AI Provider', icon: Sparkles, value: health?.ai ?? null },
  ]
  // Per-subsystem metric formatter. Returns the inline suffix that
  // sits after the status label (e.g. "Operational · 12 ms"). Keeping
  // this as one switch keeps the JSX below readable.
  function metricFor(key, status) {
    if (!status || status.ok !== true) return null
    if (key === 'api' && status.uptimeSec != null) {
      const d = Math.floor(status.uptimeSec / 86_400)
      const h = Math.floor((status.uptimeSec % 86_400) / 3600)
      const m = Math.floor((status.uptimeSec % 3600) / 60)
      const uptime = d > 0 ? `${d}d ${h}h` : h > 0 ? `${h}h ${m}m` : `${m}m`
      const mem = status.memoryMb != null ? ` · ${status.memoryMb} MB` : ''
      return ` · up ${uptime}${mem}`
    }
    if (key === 'database' && status.latencyMs != null) {
      return ` · ${status.latencyMs} ms`
    }
    if (key === 'redis' && status.latencyMs != null) {
      return ` · ${status.latencyMs} ms`
    }
    if (key === 'emailQueue' && status.queueDepth != null) {
      const failed =
        status.failed != null && status.failed > 0
          ? ` · ${formatNumber(status.failed)} failed`
          : ''
      return ` · queue ${formatNumber(status.queueDepth)}${failed}`
    }
    if ((key === 'storage' || key === 'stripe') && status.latencyMs != null) {
      return ` · ${status.latencyMs} ms`
    }
    return null
  }
  return (
    // One tile per row on a phone. At `grid-cols-2` on a 375px screen each
    // tile is ~163px wide, and a 40px icon + padding leaves ~79px for text —
    // enough to clip "Database" to "Da…" and "Operational" to "Oper…", which
    // is what QA reported. Two-up from `sm`, four-up from `lg` as before.
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {tiles.map((t) => {
        const status = t.value
        const ok = status?.ok === true
        const failing = status?.ok === false
        // ok=null is the "Not monitored" path — render muted so it
        // reads as informational, not an alarm.
        const notMonitored = status?.ok === null || status?.ok === undefined
        const dotClass = failing
          ? 'bg-[#E11D48]'
          : ok
            ? 'bg-[#059669]'
            : 'bg-[#94A3B8]'
        const statusLabel = failing
          ? 'Failing'
          : ok
            ? 'Operational'
            : notMonitored && status?.note
              ? 'Not monitored'
              : 'Not probed'
        const tone = failing ? 'rose' : ok ? 'emerald' : 'slate'
        const tones = TONE_CLASSES[tone]
        const Icon = t.icon
        const metric = metricFor(t.key, status)
        // Secondary line: metric when green, error when red, note
        // when "not monitored", "Checked …" timestamp otherwise.
        const secondary = failing
          ? status?.error ?? 'Probe failed'
          : notMonitored && status?.note
            ? status.note
            : status?.lastCheckedAt
              ? `Checked ${fmtAgo(status.lastCheckedAt)}`
              : 'No probe yet'
        return (
          <div
            key={t.key}
            className={
              'rounded-xl border p-4 flex items-center gap-3 ' +
              (notMonitored
                ? 'border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] opacity-80'
                : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)]')
            }
          >
            <div
              className={
                'h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ' +
                tones.tile
              }
            >
              <Icon size={20} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                {/* Subsystem name and its status both wrap rather than
                 *  clip — "Database" and "Email Queue" are the whole point
                 *  of the tile, so losing them to an ellipsis defeats it.
                 *  Both strings are short and bounded, so wrapping can only
                 *  add a line, never run away. */}
                <p className="text-sm font-medium text-[color:var(--color-ink-strong)] break-words">
                  {t.label}
                </p>
                <span
                  aria-hidden="true"
                  className={'h-2 w-2 rounded-full shrink-0 ' + dotClass}
                />
              </div>
              <p className="text-[13px] text-[color:var(--color-ink-muted)] break-words">
                {statusLabel}
                {metric ?? null}
              </p>
              {/* The third line CAN be an arbitrarily long probe error, so it
               *  stays bounded to one line — but now always carries the full
               *  text as a tooltip, not only when the probe is failing. */}
              <p
                className="text-[11px] text-[color:var(--color-ink-muted)] truncate"
                title={typeof secondary === 'string' ? secondary : undefined}
              >
                {secondary}
              </p>
            </div>
          </div>
        )
      })}
    </div>
  )
}
